import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Scale, BookOpen, ArrowUpRight } from "lucide-react";

/**
 * The footer.
 *
 * A World needs an edge, not a sitemap. So this does three things and stops:
 * says what the three doors are, tells the truth about what is real and what is
 * mocked, and points at the evidence. No newsletter, no social row, no
 * "Solutions" column.
 */
const DOORS = [
  { host: "endeavorum.com", name: "Endeavorum", note: "the World, and the Atlas", accent: "#f6d696" },
  { host: "playforward.app", name: "PlayForward", note: "the Pursuital Arcade", accent: "#7fd4e8" },
  { host: "stageward.app", name: "Stageward", note: "Clarity Hall", accent: "#e8c98a" },
];

const LINKS = [
  { label: "The Proof", to: "/proof", icon: ShieldCheck, note: "check the claims yourself" },
  { label: "The Ledger", to: "/ledger", icon: Scale, note: "every Token ever moved" },
  { label: "The Guidebook", to: "/guidebook", icon: BookOpen, note: "the Practices, in order" },
];

export const SiteFooter = () => {
  const nav = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative border-t border-white/10 bg-[#07090c]"
      data-testid="site-footer"
    >
      {/* a thin lit seam, so the edge of the World glows rather than just ending */}
      <div
        className="h-px w-full"
        style={{
          background:
            "linear-gradient(90deg, rgba(246,214,150,0) 0%, rgba(246,214,150,0.5) 38%, rgba(120,200,220,0.4) 68%, rgba(246,214,150,0) 100%)",
        }}
      />

      <div className="mx-auto max-w-[1500px] px-6 py-16 md:px-10 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr_1fr]">
          {/* ---- the claim */}
          <div>
            <p className="tracked mb-3 text-[10px] font-semibold text-[var(--stage-gold)]/70">
              ENDEAVORUM
            </p>
            <p className="serif text-2xl font-light leading-snug text-[#f4ecdd] md:text-[26px]">
              Adulting finally has
              <span className="italic text-[var(--stage-gold)]"> a world.</span>
            </p>
            <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-white/50">
              One identity, one Pouch, one MelzAI — across three separate domains
              sharing a single ledger. Not three apps wearing a theme.
            </p>

            <div className="mt-7 inline-flex flex-col gap-1 rounded-lg border border-[var(--stage-gold)]/25 bg-[var(--stage-gold)]/[0.06] px-4 py-3">
              <span className="tracked text-[9px] font-semibold text-[var(--stage-gold)]/80">
                BUILT FOR
              </span>
              <span className="serif text-lg leading-none text-[#f4ecdd]">
                Emergent BuilderFest
              </span>
              <span className="mono text-[10px] text-white/40">
                {year} · built in the open, in one week
              </span>
            </div>
          </div>

          {/* ---- the three doors */}
          <div>
            <p className="tracked mb-5 text-[10px] font-semibold text-white/45">
              THREE FRONT DOORS, ONE SUBSTRATE
            </p>
            <ul className="space-y-4" data-testid="footer-doors">
              {DOORS.map((d) => (
                <li key={d.host}>
                  <a
                    href={`https://${d.host}`}
                    className="group flex items-baseline gap-2"
                    data-testid={`footer-door-${d.name.toLowerCase()}`}
                  >
                    <span
                      className="text-[15px] font-medium text-white/80 transition-colors group-hover:text-[var(--stage-gold)]"
                      style={{ color: d.accent }}
                    >
                      {d.host}
                    </span>
                    <ArrowUpRight
                      size={12}
                      className="shrink-0 text-white/25 transition-transform group-hover:-translate-y-0.5 group-hover:text-[var(--stage-gold)]"
                    />
                  </a>
                  <span className="mono block text-[11px] text-white/35">{d.note}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ---- the evidence */}
          <div>
            <p className="tracked mb-5 text-[10px] font-semibold text-white/45">
              DON'T TAKE OUR WORD FOR IT
            </p>
            <ul className="space-y-4">
              {LINKS.map((l) => {
                const Icon = l.icon;
                return (
                  <li key={l.to}>
                    <button
                      type="button"
                      onClick={() => nav(l.to)}
                      data-testid={`footer-link-${l.to.replace("/", "")}`}
                      className="group flex items-start gap-3 text-left"
                    >
                      <Icon
                        size={15}
                        strokeWidth={1.8}
                        className="mt-0.5 shrink-0 text-[var(--stage-gold)]/60 transition-colors group-hover:text-[var(--stage-gold)]"
                      />
                      <span>
                        <span className="block text-[15px] font-medium text-white/80 transition-colors group-hover:text-[var(--stage-gold)]">
                          {l.label}
                        </span>
                        <span className="mono block text-[11px] text-white/35">{l.note}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* ---- the honest bit. Says what is real before anyone has to ask. */}
        <motion.div
          className="mt-14 border-t border-white/8 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <p
              className="max-w-2xl text-[13px] leading-relaxed text-white/40"
              data-testid="footer-alpha-notice"
            >
              <span className="font-semibold text-white/60">Alpha.</span> The
              identity, the Pouch, the double-entry ledger and the crossings
              between domains are all real and running against one database —
              press the buttons on{" "}
              <button
                type="button"
                onClick={() => nav("/proof")}
                className="underline decoration-white/25 underline-offset-4 transition-colors hover:text-[var(--stage-gold)]"
              >
                The Proof
              </button>{" "}
              if you would rather check than believe.{" "}
              <span className="text-white/60">Payments are mocked</span>, and MelzAI
              runs in Demo Mode unless you flip her live. Tokens carry no cash value.
            </p>

            <div className="shrink-0 md:text-right">
              <p className="mono text-[11px] text-white/30">
                © {year} Endeavorum. All rights reserved.
              </p>
              <p className="mono mt-1 text-[11px] text-white/25">
                Your words stay yours — everything exports to Markdown.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default SiteFooter;
